import * as React from "react";
import type { ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/primitives/alert-dialog";
import type { ButtonProps } from "@/shared/components/primitives/button";

export interface ColocaAlertDialogProps {
  /**
   * Element that opens the dialog — typically a `<ColocaButton>`. The dialog wires up the click handler
   * automatically; do not add your own `onClick` to the trigger. Omit it when the dialog is opened
   * programmatically (e.g. from a table row action) and drive it with `open` / `onOpenChange` instead.
   */
  trigger?: React.ReactElement;
  /** Short, specific title that tells the user what decision they are making. Keep to one line. */
  title: ReactNode;
  /** Additional context that helps the user understand the consequences of confirming. */
  description?: ReactNode;
  /**
   * Body rendered between the header and the footer, for confirmations that must show _what_ is about to
   * change — a summary of the record being deleted, or the blast radius of the action. Use block-level
   * content here; `description` renders inside a paragraph and cannot host it.
   */
  children?: ReactNode;
  /**
   * Icon or illustration rendered in the media slot above the title. Pass a Lucide icon element sized
   * `size-8`. The slot adds its own container (`size-16`, `rounded-md`, `bg-muted`) — do not wrap it
   * yourself.
   */
  media?: ReactNode;
  /** Label for the confirm button. Defaults to `"Confirm"`. */
  confirmLabel?: string;
  /**
   * Visual variant of the confirm button. Use `"destructive"` for irreversible actions (delete, revoke).
   * Defaults to `"primary"`.
   */
  confirmVariant?: ButtonProps["variant"];
  /**
   * Blocks the confirm button. Use it while the confirmed action is in flight — the dialog is controlled, so
   * it stays open on failure, and without this a second click would fire the mutation again — or when the
   * body surfaces a condition that makes confirming invalid.
   */
  confirmDisabled?: boolean;
  /** Label for the cancel button. Defaults to `"Cancel"`. Pass `null` to suppress the cancel button. */
  cancelLabel?: string | null;
  /**
   * Called when the user clicks the confirm button. Unlike cancel, confirming does **not** dismiss the dialog
   * — the handler owns that, which is what lets an async action keep the dialog open and report a failure in
   * the body. Close it from `onOpenChange` once the action succeeded.
   */
  onConfirm?: () => void;
  /** Called when the user clicks the cancel button or presses Escape. */
  onCancel?: () => void;
  /**
   * Dialog width. `"default"` scales up to `sm:max-w-lg`; `"sm"` stays at `max-w-xs` and uses a two-column
   * footer. Use `"sm"` for simple yes/no confirmations and `"default"` for dialogs with longer descriptions.
   */
  size?: "default" | "sm";
  /** Controlled open state. Pair with `onOpenChange` to manage the dialog from outside. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Modal confirmation dialog that interrupts the user flow to confirm an action.
 *
 * Use `ColocaAlertDialog` before any action that is destructive or difficult to reverse — deleting a record,
 * revoking access, cancelling a transfer. For non-blocking feedback use `ColocaAlert`; for informational
 * modals that do not require confirmation use a standard dialog component.
 *
 * Pass a `trigger` to control opening. The dialog manages its own open/close state unless you pass `open` and
 * `onOpenChange` for controlled usage.
 */
export function ColocaAlertDialog({
  trigger,
  title,
  description,
  children,
  media,
  confirmLabel = "Confirm",
  confirmVariant = "primary",
  confirmDisabled,
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  size = "default",
  open,
  onOpenChange,
}: ColocaAlertDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger && <AlertDialogTrigger render={trigger} />}
      <AlertDialogContent size={size}>
        <AlertDialogHeader>
          {media && <AlertDialogMedia>{media}</AlertDialogMedia>}
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          {cancelLabel !== null && (
            <AlertDialogCancel variant="ghost" onClick={onCancel}>
              {cancelLabel}
            </AlertDialogCancel>
          )}
          <AlertDialogAction variant={confirmVariant} disabled={confirmDisabled} onClick={onConfirm}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
